"use client"

import type { FC, ReactElement } from "react"
import type { DropdownLabelProps } from "#components/lib/inputs/dropdown/dropdownDisplay"
import type { SupportedLocales } from "#environment/i18nConfig"

import React, { useCallback, useMemo } from "react"
import { usePathname } from "next/navigation"
import { signOut } from "next-auth/react"
import { usePostHog } from "posthog-js/react"

import { UCDropdown } from "#components/lib/inputs/dropdown/ucDropdown"
import { LinkOption, Option } from "#components/option"
import SvgCog from "#svgs/account/Cog"
import SvgDocumentCertificate from "#svgs/semantic icons/DocumentCertificate"
import SvgLogoutBold from "#svgs/semantic icons/LogoutBold"
import SvgPaintingBoardBold from "#svgs/semantic icons/PaintingBoardBold"
import SvgUserIdentifierCardBold from "#svgs/semantic icons/UserIdentifierCardBold"
import { localizePath } from "#utils/localization/clientUtils"

// TODO Correct typing - url or onclick - etc
type PageItem = {
  icon: ReactElement
  name: string
  url?: string
  regex?: RegExp
  onClick?: () => void
  normalAnchorTag?: boolean
  newTab?: boolean
}

export function MobileAccountNavigationClient({ setup, navButton, locale }: { setup: boolean; navButton: ReactElement; locale: SupportedLocales }) {
  const posthog = usePostHog()
  const PAGE_ITEMS: (setup: boolean) => PageItem[] = useCallback(
    (setup: boolean) => [
      ...(!setup
        ? [
            {
              url: "/account/profile",
              icon: <SvgUserIdentifierCardBold className={"h-3 w-3"} />,
              name: "Profil",
            },
            {
              url: "/account/artworks",
              regex: /\/account\/artworks[\/.*]?/,
              icon: <SvgPaintingBoardBold className={"h-3 w-3"} />,
              name: "Kunstwerke",
            },
            {
              url: "/pdf/Rahmenvertrag-Artivo.pdf",
              icon: <SvgDocumentCertificate className={"h-3 w-3"} />,
              name: "Rahmenvertrag",
              normalAnchorTag: true,
              newTab: true,
            },
            // {
            //   url: "/account/verification",
            //   icon: <SvgShieldCheck className={"h-3 w-3"} />,
            //   name: "Verifizierung",
            // },
          ]
        : [
            {
              url: "/account/status",
              icon: <SvgCog className={"h-3 w-3"} />,
              name: "Account einrichten",
            },
          ]),

      {
        icon: <SvgLogoutBold className={"h-3 w-3"} />,
        name: "Logout",
        onClick: async () => {
          await signOut()
          // As adviced here: https://posthog.com/docs/product-analytics/identify#3-reset-after-logout
          posthog.reset()
        },
      },
    ],
    [posthog],
  )

  const pathName = usePathname()
  // Always convert pathname to english and without locale prefix for comparison with route names in nextjs
  const englishPathName = localizePath({ pathName, locale: "en" }).replace("/en", "")

  const label: FC<DropdownLabelProps> = () => navButton

  const pages = useMemo(() => PAGE_ITEMS(setup), [setup, PAGE_ITEMS])

  return (
    <UCDropdown
      DropdownLabel={label}
      selectedClassName={"text-gold-100"}
      dropdownWrapperStyles={
        "w-max !py-3 shadow-[0px_7px_15px_0px_rgba(0,0,0,0.11)] open:top-[calc(100%+10px)] rounded-2xl scale-y-50 open:scale-y-100 ease-out -translate-y-2 open:translate-y-0 -left-[200%] z-[500000]"
      }
      itemClassName={"py-1.5 px-6 text-nm flex items-center gap-x-2 focus-visible:outline-none"}>
      {pages.map(page => {
        const id = `${page.name}-${page.url}`
        return page.onClick ? (
          <Option key={id} identifier={id} selected={page.regex ? !!englishPathName.match(page.regex) : page.url === englishPathName} onClick={page.onClick}>
            {page.icon}
            {page.name}
          </Option>
        ) : (
          <LinkOption
            locale={locale}
            key={id}
            normalAnchorTag={page.normalAnchorTag}
            target={page.newTab ? "_blank" : undefined}
            identifier={id}
            href={`${page.url!}${setup ? "?setup=true" : ""}`}
            selected={page.regex ? !!englishPathName.match(page.regex) : page.url === englishPathName}>
            {page.icon}
            {page.name}
          </LinkOption>
        )
      })}
    </UCDropdown>
  )
}
